// @flow
import createApiClient from 'src/modules/seek-jobs-api-client';
import { setPageTitle } from 'src/store/location/location';
import Promise from 'bluebird';
import striptags from 'striptags';
import { isMobileUserAgent } from 'src/modules/chalice-user-agent/device-detect';
import { isEmpty, omit, get, compact } from 'lodash';
import { RESET_FITME } from 'src/store/fitme/fitme';

const SUPPORTED_MOBILE_TAGS = [
  'h2',
  'p',
  'b',
  'strong',
  'em',
  'i',
  'ul',
  'ol',
  'li',
  'br',
  'hr',
  'div',
  'a',
  'span'
];

const api = createApiClient();

export const jobSelector = ({ result }: { result: ChaliceJob }) => result;

export const PREPOPULATE_JOB_DETAILS = 'PREPOPULATE_JOB_DETAILS';
export const JOB_DETAILS_FETCH_BEGIN = 'JOB_DETAILS_FETCH_BEGIN';
export const JOB_DETAILS_FETCH_SUCCESS = 'JOB_DETAILS_FETCH_SUCCESS';
export const JOB_DETAILS_FETCH_FAILURE = 'JOB_DETAILS_FETCH_FAILURE';
export const JOB_EXPIRED = 'JOB_EXPIRED';
export const JOB_DETAILS_PAGE_LOADED = 'JOB_DETAILS_PAGE_LOADED';
export const SHARE_LINK_CLICKED = 'SHARE_LINK_CLICKED';

export const JOB_APPLICATION_FETCH_START = 'JOB_APPLICATION_FETCH_START';
export const JOB_APPLICATION_FETCH_SUCCESS = 'JOB_APPLICATION_FETCH_SUCCESS';
export const JOB_APPLICATION_FETCH_FAILURE = 'JOB_APPLICATION_FETCH_FAILURE';

const JOB_FRAUD_REPORT_SUCCESS = 'JOB_FRAUD_REPORT_SUCCESS';
const JOB_FRAUD_REPORT_FAILURE = 'JOB_FRAUD_REPORT_FAILURE';

export const SEND_JOB_SUCCESS = 'SEND_JOB_SUCCESS';
const SEND_JOB_FAILURE = 'SEND_JOB_FAILURE';

export const CIS_INTERACTION_EVENT = 'CIS_INTERACTION_EVENT';
export const CIS_IMPRESSION = 'CIS_IMPRESSION';

function transformSERPJobToJobDetails(serpJob) {
  const { location: city, area, advertiser, title = '' } = serpJob;

  return {
    ...serpJob,
    title: striptags(title),
    locationHierarchy: {
      city,
      area
    },
    advertiser: {
      ...advertiser,
      id: parseInt(advertiser.id, 10)
    }
  };
}

const initialState = {
  fraudReport: {},
  jobPending: false,
  result: null,
  pageLoadedCount: 0
};

export default function reducer(
  state: Object = initialState,
  action: any = {}
) {
  switch (action.type) {
    case JOB_DETAILS_PAGE_LOADED: {
      const { pageLoadedCount } = state;
      return {
        ...state,
        pageLoadedCount: pageLoadedCount + 1
      };
    }

    case JOB_EXPIRED: {
      const { result } = action.payload;

      return {
        ...state,
        jobPending: false,
        result: {
          ...result,
          isExpired: true
        }
      };
    }

    case JOB_DETAILS_FETCH_FAILURE: {
      return {
        ...state,
        jobPending: false
      };
    }

    case JOB_DETAILS_FETCH_BEGIN: {
      return {
        ...state,
        jobPending: true
      };
    }

    case PREPOPULATE_JOB_DETAILS:
    case JOB_DETAILS_FETCH_SUCCESS: {
      const { result: currentResult } = state;
      const { result: fetchResult } = action.payload;

      const newResult =
        action.type === PREPOPULATE_JOB_DETAILS
          ? transformSERPJobToJobDetails(fetchResult)
          : fetchResult;

      // Merge serp results to jobdetails. This reduces flash of changing content
      // on revisiting the same last job. This also passes the queryString from the SERP
      // So that we have access to serp position for the EmailFriend functionality
      const isSameJob = currentResult && currentResult.id === newResult.id;
      const result = isSameJob
        ? {
            ...currentResult,
            ...newResult
          }
        : newResult;

      const { mobileAdTemplate, hasCustomTemplate, branding } = result;

      const shouldSanitizeMobileTemplate = !isSameJob && mobileAdTemplate;
      const shouldShowBranding = !isEmpty(branding) && !hasCustomTemplate;

      const propertiesToOmit = compact([
        !shouldShowBranding && 'branding',
        !hasCustomTemplate && 'desktopAdTemplate'
      ]);

      return {
        ...state,
        fraudReport: {},
        jobPending: false,
        result: {
          // $FlowFixMe: array type is incompatible with string
          ...omit(result, propertiesToOmit),
          ...(shouldSanitizeMobileTemplate
            ? {
                mobileAdTemplate: striptags(
                  mobileAdTemplate,
                  SUPPORTED_MOBILE_TAGS
                )
              }
            : {})
        }
      };
    }

    case JOB_APPLICATION_FETCH_START: {
      return {
        ...state,
        jobApplication: null
      };
    }

    case JOB_APPLICATION_FETCH_SUCCESS: {
      const { jobApplication } = action.payload;

      return {
        ...state,
        jobApplication
      };
    }

    case JOB_APPLICATION_FETCH_FAILURE: {
      return state;
    }

    default: {
      return state;
    }
  }
}

export const prepopulateJob = (result: ChaliceJob) => (
  dispatch: Function,
  getState: Function
) => {
  dispatch({
    type: PREPOPULATE_JOB_DETAILS,
    payload: { result, getState }
  });
};

export const jobDetailsFetchFailure = ({
  error,
  job
}: {
  error: Error,
  job: Object
}) => {
  return {
    type: JOB_DETAILS_FETCH_FAILURE,
    payload: { error, result: job }
  };
};

export const fetchJob = (
  id: number | string | null,
  userAgent: string,
  locale: Locale,
  requestId: string
) => {
  const isDesktop = !isMobileUserAgent();
  return (dispatch: Function, getState: Function) => {
    dispatch({ type: JOB_DETAILS_FETCH_BEGIN });
    dispatch({ type: RESET_FITME });

    return (
      api.jobdetails
        .fetch(id, isDesktop, locale, requestId)
        // $FlowFixMe: then is missing in ChaliceJob
        .then(job => {
          dispatch({
            type: JOB_DETAILS_FETCH_SUCCESS,
            payload: {
              result: job,
              getState
            },
            meta: {
              metrics: {
                name: JOB_DETAILS_FETCH_SUCCESS,
                ...(isDesktop && !job.desktopAdTemplate
                  ? { tags: { fallbackTemplate: true } }
                  : {})
              }
            }
          });

          const { title } = job;
          dispatch(setPageTitle(title));
        })
        .catch(error => {
          const status = get(error, 'response.status');
          const data = get(error, 'response.data');

          if (status === 410) {
            dispatch({
              type: JOB_EXPIRED,
              payload: { result: data }
            });

            if (ENV.SERVER) {
              // bubble event to server render handler
              return Promise.reject(error);
            }
          } else {
            const { jobdetails } = getState();

            // Try to use prepopulated job from search.
            const job = jobSelector(jobdetails) || { id };

            dispatch(jobDetailsFetchFailure({ error, job }));

            // bubble event to server render handler
            return Promise.reject(error);
          }

          return null;
        })
    );
  };
};

export const jobDetailsPageLoaded = (
  query: SearchParams,
  jobDetailsViewedCorrelationId: string
) => (dispatch: Function, getState: Function) => {
  const {
    results: { userQueryId },
    jobdetails,
    location: { locale },
    user: { seekerId }
  } = getState();

  return dispatch({
    type: JOB_DETAILS_PAGE_LOADED,
    payload: {
      locale,
      job: jobSelector(jobdetails),
      userQueryId,
      query,
      seekerId,
      jobDetailsViewedCorrelationId
    }
  });
};

export const jobApplicationFetchStart = () => ({
  type: JOB_APPLICATION_FETCH_START
});

export const jobApplicationFetchSuccess = (jobApplication: Object) => ({
  type: JOB_APPLICATION_FETCH_SUCCESS,
  payload: { jobApplication }
});

export const jobApplicationFetchFailure = () => ({
  type: JOB_APPLICATION_FETCH_FAILURE
});

export const cisInteraction = ({
  type,
  eventType
}: {
  type: ContactTypes,
  eventType: ContactInteractionType
}) => ({
  type: CIS_INTERACTION_EVENT,
  payload: {
    type,
    eventType
  },
  meta: {
    metrics: {
      name: 'cis_interaction',
      tags: {
        cisType: type.toLowerCase(),
        cisEventType: eventType
      }
    }
  }
});

export const cisImpression = (impression: Analytics$CISImpression) => ({
  type: CIS_IMPRESSION,
  payload: impression,
  meta: {
    metrics: {
      name: 'cis_impression',
      tags: { cisMatches: impression.matches }
    }
  }
});

const getJobApplicationApi = id => api.jobdetails.jobApplication(id);

export function fetchJobApplication({
  id,
  authenticated,
  getJobApplication = getJobApplicationApi
}: {
  id: number | string | null,
  authenticated: boolean,
  getJobApplication?: Function
} = {}) {
  return (dispatch: Function) => {
    if (!authenticated) {
      return Promise.resolve(null);
    }

    dispatch(jobApplicationFetchStart());

    return getJobApplication(id)
      .then(jobApplication =>
        dispatch(jobApplicationFetchSuccess(jobApplication))
      )
      .catch(() => {
        dispatch(jobApplicationFetchFailure());
      });
  };
}

export const sendFraudReport = ({
  email,
  comment,
  reason,
  jobId
}: {
  email: string,
  comment: string,
  reason: string,
  jobId: number
}) => {
  return (dispatch: Function, getState: Function) => {
    const { emailAddress } = getState().user;

    return api.jobdetails
      .sendFraudReport({ email: email || emailAddress, comment, reason, jobId })
      .then(() => {
        dispatch({
          type: JOB_FRAUD_REPORT_SUCCESS,
          meta: {
            metrics: {
              name: JOB_FRAUD_REPORT_SUCCESS
            },
            hotjar: 'Fraud Report Success'
          }
        });
      })
      .catch(e => {
        dispatch({
          type: JOB_FRAUD_REPORT_FAILURE,
          meta: {
            metrics: {
              name: JOB_FRAUD_REPORT_FAILURE
            },
            hotjar: 'Fraud Report Failure'
          }
        });
        throw e;
      });
  };
};

export const sendJobToFriend = ({
  jobId,
  senderName,
  senderEmail,
  receiverName,
  receiverEmail
}: {
  jobId: number,
  senderName: string,
  senderEmail: string,
  receiverName: string,
  receiverEmail: string
}) => {
  return (dispatch: Function, getState: Function) => {
    const {
      location: { locale }
    } = getState();
    return api.jobdetails
      .sendJobToFriend(
        {
          jobId,
          senderName,
          senderEmail,
          receiverName,
          receiverEmail
        },
        locale
      )
      .then(() => {
        dispatch({
          type: SEND_JOB_SUCCESS,
          meta: {
            metrics: {
              name: SEND_JOB_SUCCESS
            }
          }
        });
      })
      .catch(e => {
        dispatch({
          type: SEND_JOB_FAILURE,
          meta: {
            metrics: {
              name: SEND_JOB_FAILURE
            }
          }
        });
        throw e;
      });
  };
};

export const shareLinkClicked = (shareType: string) => ({
  type: SHARE_LINK_CLICKED,
  payload: shareType
});
